<template>
  <div class="phone-input-wrapper">
    <label v-if="label" class="phone-label">{{ label }}</label>

    <div class="phone-input-group" :class="{ 'has-error': showError }">
      <!-- Country code dropdown -->
      <div class="country-dropdown" ref="dropdownRef">
        <button type="button" class="country-trigger" @click="toggleDropdown" :aria-expanded="isOpen" aria-haspopup="listbox">
          <span class="country-iso">{{ selectedCountry.iso }}</span>
          <svg class="chevron" :class="{ open: isOpen }" width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>

        <div v-if="isOpen" class="country-menu" role="listbox">
          <div class="search-box">
            <input ref="searchRef" type="text" v-model="search" placeholder="Search" class="search-input" @keydown.esc="closeDropdown" />
          </div>

          <ul class="country-list">
            <li
              v-for="country in filteredCountries"
              :key="country.iso"
              class="country-item"
              :class="{ active: country.iso === selectedCountry.iso }"
              role="option"
              :aria-selected="country.iso === selectedCountry.iso"
              @click="selectCountry(country)"
            >
              <span class="iso-badge">{{ country.iso }}</span>
              <span class="country-name">{{ country.name }}</span>
              <span class="dial-code">+{{ country.dialCode }}</span>
            </li>
            <li v-if="filteredCountries.length === 0" class="empty-state">No countries found</li>
          </ul>
        </div>
      </div>

      <!-- Phone number input -->
      <input type="tel" class="phone-number-input" :value="displayValue" :placeholder="computedPlaceholder" @input="onInput" @blur="onBlur" />
    </div>

    <!-- Validation status -->
    <div v-if="touched && nationalNumber" class="status-line">
      <span v-if="isValid" class="status validated">Validated</span>
      <span v-else class="status invalid">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType, getExampleNumber } from 'libphonenumber-js'
import examples from 'libphonenumber-js/examples.mobile.json'

// Localised English country names from ISO codes (supported by all modern
// browsers). Falls back to the ISO code if Intl.DisplayNames is unavailable.
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' })
  } catch (e) {
    return null
  }
})()

function countryName(iso) {
  if (regionNames) {
    try {
      const n = regionNames.of(iso)
      if (n && n !== iso) return n
    } catch (e) {
      /* fall through */
    }
  }
  return iso
}

// The full set of dialable regions comes straight from libphonenumber-js,
// so the list, dial codes, validation and formatting all stay in sync.
const countries = getCountries()
  .map(iso => ({ iso, name: countryName(iso), dialCode: getCountryCallingCode(iso) }))
  .sort((a, b) => a.name.localeCompare(b.name))

const FALLBACK_ISO = countries.some(c => c.iso === 'SG') ? 'SG' : countries[0].iso

export default {
  name: 'PhoneInput',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    defaultCountry: {
      type: String,
      default: 'SG',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    // Optional Vuelidate field validator (e.g. $v.form.phone).
    // When provided, it will be $reset() on input changes so stale
    // errors clear as soon as the user starts editing.
    validator: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      countries,
      isOpen: false,
      search: '',
      touched: false,
      // National significant number, digits only, as entered by the user.
      nationalNumber: '',
      selectedIso: countries.some(c => c.iso === this.defaultCountry) ? this.defaultCountry : FALLBACK_ISO,
    }
  },

  computed: {
    filteredCountries() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.countries
      return this.countries.filter(c => c.name.toLowerCase().includes(q) || c.iso.toLowerCase().includes(q) || c.dialCode.includes(q))
    },

    selectedCountry() {
      return this.countries.find(c => c.iso === this.selectedIso) || this.countries[0]
    },

    // Parsed representation of the current national number for the selected
    // country. null while the field is empty.
    parsed() {
      if (!this.nationalNumber) return null
      return parsePhoneNumberFromString(this.nationalNumber, this.selectedIso) || null
    },

    displayValue() {
      const dial = this.selectedCountry.dialCode
      if (!this.nationalNumber) return `+${dial} `
      const formatted = new AsYouType(this.selectedIso).input(this.nationalNumber)
      return `+${dial} ${formatted}`
    },

    // A real example number for the selected country, used as a placeholder
    // hint unless the caller supplied an explicit placeholder.
    computedPlaceholder() {
      if (this.placeholder) return this.placeholder
      try {
        const ex = getExampleNumber(this.selectedIso, examples)
        return ex ? ex.formatNational() : ''
      } catch (e) {
        return ''
      }
    },

    isValid() {
      return this.parsed ? this.parsed.isValid() : false
    },

    // E.164 value we surface to the parent (e.g. "+6591234567"), or '' when
    // the current input is empty/invalid.
    e164() {
      return this.parsed && this.parsed.isValid() ? this.parsed.number : ''
    },

    errorMessage() {
      if (!this.nationalNumber) return 'Phone number is required'
      return 'Invalid phone number for ' + this.selectedCountry.name
    },

    showError() {
      return Boolean(this.touched && this.nationalNumber && !this.isValid)
    },
  },

  watch: {
    value(v) {
      // Ignore our own emitted value; only re-parse genuinely external changes.
      if ((v || '') === this.e164) return
      this.parseIncoming(v || '')
    },
  },

  mounted() {
    if (this.value) this.parseIncoming(this.value)
    document.addEventListener('click', this.onOutsideClick)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick)
  },

  methods: {
    parseIncoming(value) {
      const parsed = value ? parsePhoneNumberFromString(value) : null
      if (parsed && parsed.country) {
        this.selectedIso = parsed.country
        this.nationalNumber = parsed.nationalNumber
        return
      }
      // Couldn't map to a country — keep the digits under the current country.
      this.nationalNumber = (value || '').replace(/\D/g, '')
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchRef) this.$refs.searchRef.focus()
        })
      }
    },

    closeDropdown() {
      this.isOpen = false
      this.search = ''
    },

    selectCountry(country) {
      this.selectedIso = country.iso
      this.closeDropdown()
      this.resetValidator()
      this.emitUpdate()
    },

    onInput(e) {
      const raw = e.target.value
      // Strip the dial code prefix if the user typed over it, then keep digits.
      const stripped = raw.replace(new RegExp(`^\\+?\\s*${this.selectedCountry.dialCode}\\s*`), '')
      this.nationalNumber = stripped.replace(/\D/g, '')
      this.touched = true
      this.resetValidator()
      this.emitUpdate()
    },

    resetValidator() {
      if (this.validator && typeof this.validator.$reset === 'function') {
        this.validator.$reset()
      }
    },

    onBlur() {
      this.touched = true
    },

    onOutsideClick(e) {
      const dropdown = this.$refs.dropdownRef
      if (dropdown && !dropdown.contains(e.target)) {
        this.closeDropdown()
      }
    },

    emitUpdate() {
      const val = this.e164
      // Only emit a value when valid — invalid/empty input emits ''
      // so the parent's required/v-model validation correctly fails.
      this.$emit('input', val)
      this.$emit('validation', {
        isValid: this.isValid,
        country: this.selectedIso,
        e164: val,
      })
    },
  },
}
</script>

<style scoped>
.phone-input-wrapper {
}

.phone-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #33475b;
  margin-bottom: 6px;
}

.phone-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid #cbd6e2;
  border-radius: 0.475rem;
  background: #fafafa;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.phone-input-group:focus-within {
  border-color: #0091ae;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.phone-input-group.has-error {
  border-color: #c3554e;
}

.phone-input-group.has-error:focus-within {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Country dropdown */
.country-dropdown {
  position: relative;
  border-right: 1px solid #cbd6e2;
}

.country-trigger {
  height: 100%;
  padding: 0 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #33475b;
  min-height: 38px;
}

.country-trigger:hover {
  background: #f5f8fa;
}

.country-iso {
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.chevron {
  transition: transform 0.15s;
  color: #506e91;
}

.chevron.open {
  transform: rotate(180deg);
}

.country-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 280px;
  background: #fff;
  border: 1px solid #cbd6e2;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.search-box {
  padding: 8px;
  border-bottom: 1px solid #eaf0f6;
}

.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #cbd6e2;
  border-radius: 3px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.search-input:focus {
  border-color: #0091ae;
  box-shadow: 0 0 0 2px rgba(0, 145, 174, 0.2);
}

.country-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 260px;
  overflow-y: auto;
}

.country-item {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: #33475b;
  cursor: pointer;
}

.country-item:hover,
.country-item.active {
  background: #f5f8fa;
}

.iso-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7c98b6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.country-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dial-code {
  color: #506e91;
  font-size: 13px;
}

.empty-state {
  padding: 16px;
  text-align: center;
  color: #7c98b6;
  font-size: 13px;
}

/* Phone input */
.phone-number-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 12px;
  font-size: 14px;
  color: #33475b;
  background: transparent;
  min-height: 38px;
}

.phone-number-input::placeholder {
  color: #7c98b6;
}

/* Status */
.status-line {
  margin-top: 6px;
}

.status {
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 3px;
}

.status.validated {
  background: #e5f5f8;
  color: #0091ae;
}

.status.invalid {
  background: #fdeceb;
  color: #c3554e;
}
</style>
